import { openCropDialog } from './crop-control.js';
import { formatZoneSize } from '@pz/scene';
import { createPanels, el, iconBtn, type DesignerCtx, type PanelHost, type ToolId } from './panels.js';
import type { CreateShell, DesignerShell, ShellOptions } from './shell-types.js';
import { createToolbar, type ToolbarHandle } from './toolbar.js';
import { createBottomBar, type BottomBarHandle } from './bottom-bar.js';
import { renderReview } from './review.js';
import { openFontPicker, type FontPickerHandle } from './controls.js';
import { openDialog, closeOverlays } from './overlay.js';
import { fillText, isBlocking, problemMessage } from './texts.js';
import { toolFor } from './commands.js';
import { startReplace } from './image-panel.js';
import { nextDesignerStep, type DesignerStage, type DesignerStep } from './steps.js';
import { createArrangeStrip, type ArrangeStripHandle } from './arrange-strip.js';

/**
 * The desktop chrome: the reference editor's own shape, in Serbian.
 *
 * Header (52 px) · contextual toolbar (44 px, only with a selection) · body
 * [rail 64 | floating panel card 320 | stage | side thumbnails 96] · the
 * bottom-left pill. Nothing in here knows how a design changes — it opens
 * panels, reads the store and calls back into the engine — which is what lets
 * the phone shell be a completely different set of nodes over the same state.
 *
 * Two tabs, exactly as the reference has them: Dizajn is the editor, Pregled
 * swaps the panel card for the review column and puts the whole composition on
 * the canvas. The primary button changes with the tab, because "Dalje" and
 * "Gotovo" are different promises.
 */

export const createDesktopShell: CreateShell = (options: ShellOptions): DesignerShell => {
  const ctx = options.ctx;
  const texts = ctx.texts;

  const root = el('div', 'pzd-shell is-desktop');

  // ── Header ───────────────────────────────────────────────────────────────
  const head = el('div', 'pzd-head');

  const exitBtn = el('button', 'pzd-exit');
  exitBtn.type = 'button';
  exitBtn.append(el('span', 'pzd-exit-x', '✕'), el('span', undefined, texts.saveExit));
  exitBtn.addEventListener('click', () => options.actions.done());

  const copy = el('div', 'pzd-head-copy');
  const title = el('div', 'pzd-title', currentSurfaceName() ?? texts.title);
  const sub = el('div', 'pzd-sub', fillText(texts.printSize, { size: formatZoneSize(ctx.config.sizeMm) }));
  copy.append(title, sub);

  const savedChip = el('span', 'pzd-saved');
  savedChip.append(el('i', 'pzd-dot'), el('span', undefined, texts.savedMark));

  const tabs = el('div', 'pzd-tabs is-head');
  tabs.setAttribute('role', 'tablist');
  const productOptions = options.productOptions;
  const designTab = headTab(texts.tabDesign, () => setStage('design'));
  const optionsTab = headTab(productOptions?.title ?? texts.tabOptions, () => setStage('options'));
  const reviewTab = headTab(texts.tabReview, () => setStage('review'));
  tabs.append(designTab, ...(productOptions ? [optionsTab] : []), reviewTab);

  const priceChip = el('span', 'pzd-chip is-price');
  const problemChip = el('button', 'pzd-chip is-problem');
  problemChip.type = 'button';
  problemChip.hidden = true;
  problemChip.addEventListener('click', () => options.actions.explainFirstProblem());

  const undoBtn = iconBtn('undo', texts.undo, () => options.actions.undo());
  const redoBtn = iconBtn('redo', texts.redo, () => options.actions.redo());
  const previewBtn = iconBtn('eye', texts.previewOnProduct, () => showProductPreview());
  const primary = el('button', 'pzd-primary', texts.nextReview);
  primary.type = 'button';
  primary.addEventListener('click', () => {
    const step = nextStep();
    if (step.kind === 'surface') options.switchSurface?.(step.id);
    else if (step.kind === 'options') setStage('options');
    else if (step.kind === 'review') setStage('review');
    else options.actions.done();
  });

  const headActions = el('div', 'pzd-head-actions');
  headActions.append(priceChip, problemChip, undoBtn, redoBtn, previewBtn, primary);
  head.append(exitBtn, copy, savedChip, tabs, headActions);

  // ── Body ─────────────────────────────────────────────────────────────────
  const panels: PanelHost = createPanels(ctx, options.defs);
  const toolbar: ToolbarHandle = createToolbar(ctx, {
    openPanel: (tool) => panels.open(tool),
    openFonts: () => openFonts(),
    replaceImage: (layerId) => {
      startReplace(ctx, layerId);
      panels.open('image');
    },
    // Zazzle crops in place; a turned or straightened photo gets the dialog.
    cropImage: layerId => {
      if (!options.canvas.startInlineCrop?.(layerId)) openCropDialog(ctx, layerId);
    },
  });

  const body = el('div', 'pzd-body');
  const sides = el('div', 'pzd-sides');
  const review = el('div', 'pzd-review');
  review.hidden = true;
  // The Options column takes the review column's slot: both replace the tool
  // panel beside the canvas, and never show together.
  const optionsPane = el('div', 'pzd-review pzd-options');
  optionsPane.hidden = true;
  body.append(panels.rail, panels.panel, options.canvas.element, review, optionsPane, sides);

  const bottom: BottomBarHandle = createBottomBar(ctx);
  options.canvas.element.append(bottom.element);
  // Rasporedi: the shopper's own design, rearranged, one tap each. It sits in
  // the canvas column under the stage, so the zone is fitted above it.
  const arrange: ArrangeStripHandle = createArrangeStrip(ctx, {
    onToggle: (visible) => {
      options.canvas.element.classList.toggle('has-arrange', visible);
      options.canvas.relayout();
    },
    onLayout: (height) => setArrangeHeight(height),
  });
  options.canvas.element.append(arrange.element);
  // The floating zoom bar sits just above the row, whatever its height.
  function setArrangeHeight(height: number): void {
    if (height > 0) options.canvas.element.style.setProperty('--pzd-arrange-h', `${height}px`);
  }
  const arrangeSize = typeof ResizeObserver === 'function'
    ? new ResizeObserver(() => setArrangeHeight(arrange.element.offsetHeight))
    : undefined;
  arrangeSize?.observe(arrange.element);

  root.append(head, toolbar.element, body);

  let fontPicker: FontPickerHandle | undefined;
  let destroyed = false;

  // ── Tabs and panels ──────────────────────────────────────────────────────

  function headTab(label: string, onPick: () => void): HTMLButtonElement {
    const tab = el('button');
    tab.type = 'button';
    tab.setAttribute('role', 'tab');
    tab.textContent = label;
    tab.addEventListener('click', onPick);
    return tab;
  }

  function setReview(on: boolean): void {
    if (on) leaveOptions();
    arrange.setSuppressed(on);
    ctx.setView({ review: on });
    syncTabs();
    if (on) {
      panels.close();
      paintReview();
    }
    review.hidden = !on;
    toolbar.refresh();
    options.canvas.relayout();
  }

  // ── The Options step ─────────────────────────────────────────────────────
  let optionsOpen = false;
  let unmountOptions: (() => void) | undefined;

  function enterOptions(): void {
    if (!productOptions) return;
    if (ctx.view.review) setReview(false);
    panels.close();
    optionsOpen = true;
    optionsPane.textContent = '';
    optionsPane.append(
      el('h3', 'pzd-review-title', texts.optionsTitle),
      el('p', 'pzd-hint', texts.optionsHint),
    );
    const slot = el('div', 'pzd-options-slot');
    optionsPane.append(slot);
    try {
      unmountOptions = productOptions.mount(slot);
    } catch {
      unmountOptions = undefined;
    }
    optionsPane.hidden = false;
    arrange.setSuppressed(true);
    syncTabs();
    toolbar.refresh();
    options.canvas.relayout();
  }

  function leaveOptions(): void {
    if (!optionsOpen) return;
    optionsOpen = false;
    const unmount = unmountOptions;
    unmountOptions = undefined;
    try {
      unmount?.();
    } catch {
      /* the host's controls are the host's to put back */
    }
    optionsPane.hidden = true;
    optionsPane.textContent = '';
    arrange.setSuppressed(ctx.view.review);
    syncTabs();
    options.canvas.relayout();
  }

  function stage(): DesignerStage {
    return ctx.view.review ? 'review' : optionsOpen ? 'options' : 'design';
  }

  function setStage(next: DesignerStage): void {
    if (next === 'review') setReview(true);
    else if (next === 'options') enterOptions();
    else {
      leaveOptions();
      setReview(false);
    }
  }

  function nextStep(): DesignerStep {
    return nextDesignerStep(stage(), options.surfaces ?? [], Boolean(productOptions));
  }

  function syncTabs(): void {
    const now = stage();
    for (const [tab, own] of [[designTab, 'design'], [optionsTab, 'options'], [reviewTab, 'review']] as const) {
      tab.classList.toggle('is-on', now === own);
      tab.setAttribute('aria-selected', String(now === own));
    }
    const step = nextStep();
    primary.textContent = step.kind === 'surface'
      ? fillText(texts.nextSurface, { name: step.name })
      : step.kind === 'options'
        ? texts.nextOptions
        : step.kind === 'review' ? texts.nextReview : texts.done;
    root.classList.toggle('is-review-tab', now === 'review');
    root.classList.toggle('is-options-tab', now === 'options');
  }

  function paintReview(): void {
    review.textContent = '';
    renderReview(review, ctx, {
      multiSurface: (options.surfaces?.length ?? 0) > 1,
      fix: (problem) => {
        setReview(false);
        if (!problem.layerId) {
          options.actions.explainFirstProblem();
          return;
        }
        const layer = ctx.store.value().layers.find((item) => item.id === problem.layerId);
        if (!layer) return;
        ctx.setHidden(layer.id, false);
        ctx.dispatch({ type: 'select', id: layer.id });
        panels.open(toolFor(layer));
      },
      done: () => options.actions.done(),
    });
  }

  /**
   * The font list replaces the panel card, exactly as the reference's Fonts
   * dialog replaces its rail panel: it is a long list and it belongs where the
   * eye already is, not in a modal over the canvas.
   */
  function openFonts(): void {
    const layer = ctx.selected();
    if (!layer || layer.kind !== 'text') return;
    panels.open('text');
    fontPicker?.destroy();
    fontPicker = openFontPicker({
      ctx,
      fontId: layer.fontId,
      host: panels.panel,
      onPick: (fontId) => {
        void ctx.fonts.ensure(fontId).catch(() => undefined);
        ctx.dispatch({ type: 'update', id: layer.id, patch: { fontId } });
        toolbar.refresh();
      },
      onClose: () => {
        fontPicker?.destroy();
        fontPicker = undefined;
      },
    });
  }

  function showProductPreview(): void {
    const preview = options.actions.previewProduct;
    if (!preview) {
      setReview(!ctx.view.review);
      return;
    }
    openDialog({
      ctx,
      title: texts.previewOnProduct,
      className: 'is-preview',
      build: (into) => {
        into.append(el('div', 'pzd-hint', texts.loading));
        void preview(ctx.store.value()).then((url) => {
          if (destroyed) return;
          into.textContent = '';
          if (!url) {
            into.append(el('div', 'pzd-hint', texts.noResults));
            return;
          }
          const picture = el('img', 'pzd-preview-img');
          picture.src = url;
          picture.alt = texts.previewOnProduct;
          into.append(picture);
        }).catch(() => {
          if (!destroyed) into.textContent = texts.noResults;
        });
      },
    });
  }

  // ── Side thumbnails ──────────────────────────────────────────────────────

  function currentSurfaceName(): string | undefined {
    return options.surfaces?.find((surface) => surface.current)?.name;
  }

  function paintSides(): void {
    sides.textContent = '';
    const list = options.surfaces ?? [];
    if (list.length < 2) {
      sides.hidden = true;
      return;
    }
    sides.hidden = false;
    sides.append(el('div', 'pzd-lbl', texts.sidesTitle));
    for (const surface of list) {
      const button = el('button', `pzd-side${surface.current ? ' is-on' : ''}`);
      button.type = 'button';
      button.setAttribute('aria-pressed', String(surface.current));
      button.append(el('div', 'pzd-side-thumb'), el('span', undefined, surface.name));
      button.addEventListener('click', () => {
        if (surface.current) return;
        options.switchSurface?.(surface.id);
      });
      sides.append(button);
    }
  }

  // ── Header state ─────────────────────────────────────────────────────────

  function sync(): void {
    undoBtn.disabled = !options.actions.canUndo();
    redoBtn.disabled = !options.actions.canRedo();
    const price = options.actions.price();
    priceChip.hidden = !price;
    if (price) {
      priceChip.textContent = fillText(texts.price, { price });
      priceChip.setAttribute('aria-label', fillText(texts.price, { price }));
    }
    const blocking = options.actions.problems().filter(isBlocking);
    problemChip.hidden = blocking.length === 0;
    if (blocking[0]) {
      problemChip.textContent = String(blocking.length);
      problemChip.title = problemMessage(blocking[0]);
      problemChip.setAttribute('aria-label', problemMessage(blocking[0]));
    }
    title.textContent = currentSurfaceName() ?? texts.title;
    previewBtn.hidden = !options.actions.previewProduct && ctx.view.review;
    panels.sync();
    bottom.sync();
    syncTabs();
    paintSides();
  }

  const stopSaved = options.actions.onSaved((saved) => {
    savedChip.classList.toggle('is-pending', !saved);
    const label = savedChip.querySelector('span');
    if (label) label.textContent = saved ? texts.savedMark : texts.savingMark;
  });

  syncTabs();
  paintSides();
  sync();

  return {
    layout: 'desktop',
    element: root,
    open(tool: ToolId) {
      if (ctx.view.review) setReview(false);
      leaveOptions();
      panels.open(tool);
    },
    close() {
      fontPicker?.destroy();
      fontPicker = undefined;
      panels.close();
    },
    current: () => panels.current(),
    refresh() {
      panels.refresh();
      toolbar.refresh();
      if (ctx.view.review) paintReview();
      sync();
    },
    sync,
    relayout() {
      options.canvas.relayout();
      window.setTimeout(() => toolbar.refresh(), 0);
    },
    destroy() {
      destroyed = true;
      leaveOptions();
      stopSaved();
      closeOverlays(ctx);
      fontPicker?.destroy();
      toolbar.destroy();
      bottom.destroy();
      arrangeSize?.disconnect();
      arrange.destroy();
      panels.destroy();
      root.remove();
    },
  };
};
